import React, { useState, useEffect } from 'react';
import { branding } from '../../constants/branding';
import { apiService } from '../../services/apiService';

interface BlockchainInfo {
  name: string;
  displayName: string;
  symbol: string;
  addressPrefix: string;
}

interface KYTAnalysis {
  address: string;
  blockchain: string;
  addressType: string;
  riskScore: number;
  riskLevel: string;
  isSanctioned: boolean;
  isExchange: boolean;
  exchangeName?: string;
  totalReceived: number;
  totalSent: number;
  netBalance: number;
  transactionCount: number;
  firstSeen: string;
  lastUpdated: string;
}

const RetailKYT: React.FC = () => {
  const [address, setAddress] = useState('');
  const [selectedBlockchain, setSelectedBlockchain] = useState('ETHEREUM');
  const [blockchains, setBlockchains] = useState<BlockchainInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState<KYTAnalysis | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [validation, setValidation] = useState<any>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Load supported blockchains on component mount
  useEffect(() => {
    fetchSupportedBlockchains();
  }, []);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchSupportedBlockchains = async () => {
    try {
      const response = await apiService.get('/api/admin/kyt/blockchains');
      if (response.success) {
        setBlockchains(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch blockchains:', err);
    }
  };

  const validateAndAnalyze = async () => {
    if (!address.trim()) {
      setError('Please enter an address');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // First validate the address
      const validationResponse = await apiService.post('/api/admin/kyt/validate', {
        address: address.trim(),
        blockchain: selectedBlockchain
      });

      if (!validationResponse.success || !validationResponse.data.isValid) {
        setError('Invalid address format');
        return;
      }

      setValidation(validationResponse.data);

      // Then analyze the address
      const analysisResponse = await apiService.post('/api/admin/kyt/analyze', {
        address: address.trim(),
        blockchain: selectedBlockchain
      });

      if (analysisResponse.success) {
        setAnalysis(analysisResponse.data);
      } else {
        setError('Address analysis failed');
      }
    } catch (err) {
      setError('Error analyzing address');
      console.error('Analysis error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getRiskLevelColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'LOW': return '#4CAF50';
      case 'MEDIUM': return '#FF9800';
      case 'HIGH': return '#F44336';
      case 'CRITICAL': return '#9C27B0';
      default: return '#9E9E9E';
    }
  };

  const getRiskLevelIcon = (riskLevel: string) => {
    switch (riskLevel) {
      case 'LOW': return '✅';
      case 'MEDIUM': return '⚠️';
      case 'HIGH': return '🚨';
      case 'CRITICAL': return '🚨';
      default: return '❓';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 8
    }).format(amount);
  };

  return (
    <div style={{ 
      maxWidth: 800, 
      margin: '0 auto', 
      padding: isMobile ? 16 : 20,
      width: '100%',
      boxSizing: 'border-box'
    }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ color: branding.primaryColor, marginBottom: 8 }}>
          Address Risk Check
        </h1>
        <p style={{ color: '#666', margin: 0 }}>
          Check the risk level and compliance status of any cryptocurrency address
        </p>
      </div>

      {/* Address Input Section */}
      <div style={{ 
        backgroundColor: 'white', 
        borderRadius: 12, 
        border: `1px solid ${branding.borderColor}`,
        padding: isMobile ? 16 : 24,
        marginBottom: 24,
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <h3 style={{ margin: '0 0 16px 0' }}>Enter Address to Check</h3>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : '1fr 200px 120px', 
          gap: 12, 
          marginBottom: 16,
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <input
            type="text"
            placeholder="Enter cryptocurrency address..."
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            style={{
              padding: '12px 16px',
              border: `1px solid ${branding.borderColor}`,
              borderRadius: 6,
              fontSize: 14,
              width: '100%',
              boxSizing: 'border-box'
            }}
          />
          
          <select
            value={selectedBlockchain}
            onChange={(e) => setSelectedBlockchain(e.target.value)}
            style={{
              padding: '12px 16px',
              border: `1px solid ${branding.borderColor}`,
              borderRadius: 6,
              fontSize: 14,
              width: '100%',
              boxSizing: 'border-box'
            }}
          >
            {blockchains.map(blockchain => (
              <option key={blockchain.name} value={blockchain.name}>
                {blockchain.displayName}
              </option>
            ))}
          </select>
          
          <button
            onClick={validateAndAnalyze}
            disabled={loading || !address.trim()}
            style={{
              padding: '12px 16px',
              backgroundColor: branding.primaryColor,
              color: 'white',
              border: 'none',
              borderRadius: 6,
              cursor: loading || !address.trim() ? 'not-allowed' : 'pointer',
              opacity: loading || !address.trim() ? 0.6 : 1,
              width: isMobile ? '100%' : 'auto',
              fontSize: 14,
              whiteSpace: 'nowrap'
            }}
          >
            {loading ? 'Checking...' : 'Check Risk'}
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div style={{ 
            padding: 12, 
            backgroundColor: '#FFEBEE',
            border: '1px solid #F44336',
            borderRadius: 6,
            color: '#C62828',
            marginBottom: 16,
            width: '100%',
            boxSizing: 'border-box',
            wordBreak: 'break-word'
          }}>
            {error}
          </div>
        )}

        {/* Validation Result */}
        {validation && (
          <div style={{ 
            padding: 12, 
            borderRadius: 6, 
            backgroundColor: validation.isValid ? '#E8F5E8' : '#FFEBEE',
            border: `1px solid ${validation.isValid ? '#4CAF50' : '#F44336'}`,
            marginBottom: 16,
            width: '100%',
            boxSizing: 'border-box',
            wordBreak: 'break-word'
          }}>
            <div style={{ 
              color: validation.isValid ? '#2E7D32' : '#C62828',
              fontWeight: 600,
              marginBottom: 4
            }}>
              {validation.isValid ? '✅ Valid Address' : '❌ Invalid Address'}
            </div>
            <div style={{ fontSize: 12, color: '#666' }}>
              Blockchain: {validation.blockchain}
            </div>
          </div>
        )}
      </div>

      {/* Analysis Results */}
      {analysis && (
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: 12, 
          border: `1px solid ${branding.borderColor}`,
          padding: isMobile ? 16 : 24,
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <h3 style={{ margin: '0 0 20px 0' }}>Risk Assessment Results</h3>
          
          {/* Risk Score Card */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: isMobile ? 12 : 20, 
            marginBottom: 24,
            width: '100%',
            boxSizing: 'border-box'
          }}>
            <div style={{ 
              padding: 20, 
              backgroundColor: '#f8f9fa', 
              borderRadius: 8, 
              textAlign: 'center'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>Risk Level</h4>
              <div style={{ fontSize: 24, marginBottom: 4 }}>
                {getRiskLevelIcon(analysis.riskLevel)}
              </div>
              <div style={{ 
                fontSize: 18, 
                fontWeight: 600, 
                color: getRiskLevelColor(analysis.riskLevel)
              }}>
                {analysis.riskLevel}
              </div>
              <div style={{ 
                fontSize: 14, 
                color: getRiskLevelColor(analysis.riskLevel),
                fontWeight: 600
              }}>
                {analysis.riskScore}%
              </div>
            </div>

            <div style={{ 
              padding: 20, 
              backgroundColor: '#f8f9fa', 
              borderRadius: 8, 
              textAlign: 'center'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>Address Type</h4>
              <div style={{ fontSize: 18, fontWeight: 600, color: branding.primaryColor }}>
                {analysis.addressType}
              </div>
              <div style={{ fontSize: 12, color: '#666' }}>
                {analysis.isExchange ? `Exchange: ${analysis.exchangeName}` : 'Regular Address'}
              </div>
            </div>

            <div style={{ 
              padding: 20, 
              backgroundColor: '#f8f9fa', 
              borderRadius: 8, 
              textAlign: 'center'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>Compliance Status</h4>
              <div style={{ 
                fontSize: 18, 
                fontWeight: 600, 
                color: analysis.isSanctioned ? '#F44336' : '#4CAF50'
              }}>
                {analysis.isSanctioned ? '🚨 SANCTIONED' : '✅ CLEAR'}
              </div>
              <div style={{ fontSize: 12, color: '#666' }}>
                {analysis.isSanctioned ? 'On sanctions list' : 'No sanctions found'}
              </div>
            </div>

            <div style={{ 
              padding: 20, 
              backgroundColor: '#f8f9fa', 
              borderRadius: 8, 
              textAlign: 'center'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>Activity Level</h4>
              <div style={{ fontSize: 24, fontWeight: 600, color: branding.primaryColor }}>
                {analysis.transactionCount.toLocaleString()}
              </div>
              <div style={{ fontSize: 12, color: '#666' }}>
                Total Transactions
              </div>
            </div>
          </div>

          {/* Financial Summary */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(150px, 1fr))', 
            gap: isMobile ? 12 : 16, 
            marginBottom: 24,
            width: '100%',
            boxSizing: 'border-box'
          }}>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Total Received</div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>
                {formatCurrency(analysis.totalReceived)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Total Sent</div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>
                {formatCurrency(analysis.totalSent)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Net Balance</div>
              <div style={{ 
                fontSize: 16, 
                fontWeight: 600,
                color: analysis.netBalance >= 0 ? '#4CAF50' : '#F44336'
              }}>
                {formatCurrency(analysis.netBalance)}
              </div>
            </div>
          </div>

          {/* Risk Recommendations */}
          <div style={{ 
            padding: 16, 
            backgroundColor: analysis.riskLevel === 'HIGH' || analysis.riskLevel === 'CRITICAL' ? '#FFEBEE' : 
                           analysis.riskLevel === 'MEDIUM' ? '#FFF3E0' : '#E8F5E8',
            border: `1px solid ${analysis.riskLevel === 'HIGH' || analysis.riskLevel === 'CRITICAL' ? '#F44336' : 
                               analysis.riskLevel === 'MEDIUM' ? '#FF9800' : '#4CAF50'}`,
            borderRadius: 8,
            width: '100%',
            boxSizing: 'border-box',
            wordBreak: 'break-word'
          }}>
            <h4 style={{ 
              margin: '0 0 8px 0', 
              color: analysis.riskLevel === 'HIGH' || analysis.riskLevel === 'CRITICAL' ? '#C62828' : 
                    analysis.riskLevel === 'MEDIUM' ? '#E65100' : '#2E7D32'
            }}>
              {analysis.riskLevel === 'HIGH' || analysis.riskLevel === 'CRITICAL' ? '🚨 High Risk Alert' :
               analysis.riskLevel === 'MEDIUM' ? '⚠️ Medium Risk Warning' : '✅ Low Risk'}
            </h4>
            <div style={{ 
              fontSize: 14,
              color: analysis.riskLevel === 'HIGH' || analysis.riskLevel === 'CRITICAL' ? '#C62828' : 
                    analysis.riskLevel === 'MEDIUM' ? '#E65100' : '#2E7D32'
            }}>
              {analysis.riskLevel === 'HIGH' || analysis.riskLevel === 'CRITICAL' ? 
                'This address poses significant compliance risks. Avoid transactions and report to compliance team.' :
               analysis.riskLevel === 'MEDIUM' ? 
                'This address shows some risk indicators. Exercise caution and consider enhanced due diligence.' :
                'This address appears to be low risk for normal transactions.'}
            </div>
          </div>

          {/* Additional Info */}
          <div style={{ marginTop: 20, fontSize: 12, color: '#666' }}>
            <div>First seen: {new Date(analysis.firstSeen).toLocaleDateString()}</div>
            <div>Last updated: {new Date(analysis.lastUpdated).toLocaleDateString()}</div>
            <div>Analysis timestamp: {new Date().toLocaleString()}</div>
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <div style={{ 
        marginTop: 24,
        padding: 16, 
        backgroundColor: '#F5F5F5',
        border: '1px solid #E0E0E0',
        borderRadius: 8,
        fontSize: 12,
        color: '#666',
        width: '100%',
        boxSizing: 'border-box',
        wordBreak: 'break-word'
      }}>
        <strong>Disclaimer:</strong> This risk assessment is for informational purposes only and should not be considered as financial or legal advice. 
        Always conduct your own due diligence before engaging in cryptocurrency transactions.
      </div>
    </div>
  );
};

export default RetailKYT;
